/**
 * ScriptLoader is used to dynamically load script files and get a callback once
 * all of them has completed loading.
 * This way the calling script can be sure that the code inside the files has
 * been loaded and is available for execution.
 *
 * Used to make games avoid loading unused code modules while still being able
 * to keep a unified loading index file for the Quickspin games.
 */
module com.quickspin.util {

	export class ScriptLoader {

		private _sources:any[] = [];
		private _callback:any = undefined;
		private _errorCallback:any;
		private _loadCount:number = 0;

		/**
		 * Add a script file to be loaded.
		 * @param path Where is the file located.
		 * @param fileType What type of script is it.
		 */
		public add(path:string, fileType:string = "js"):void {

			let script:HTMLElement;

			switch (fileType) {
				case "js":
					this.preload(path, "script");
					script = document.createElement("script");
					script.addEventListener("load", (e:Event) => {
						this.onLoad(e);
					});
					script.addEventListener("error", (e:ErrorEvent) => {
						this.onError(e);
					});
					script.setAttribute("type", "text/javascript");
					script.setAttribute("src", path);
					this._sources.push(script);
					break;
				case "css":
					this.preload(path, "style");
					script = document.createElement("link");
					script.addEventListener("load", (e:Event) => {
						this.onLoad(e);
					});
					script.addEventListener("error", (e:ErrorEvent) => {
						this.onError(e);
					});
					script.setAttribute("rel", "stylesheet");
					script.setAttribute("type", "text/css");
					script.setAttribute("href", path);
					this._sources.push(script);
					break;
				case "pre-load-image":
					this.preload(path, "image");
					break;
				case "pre-load-audio":
					this.preload(path, "audio");
					break;
				case "pre-load-data":
					this.preload(path, "fetch");
					break;
				case "pre-load-css":
					this.preload(path, "style");
					break;
				default:
					throw new Error("ScriptLoader.add: Unsupported script type: " + fileType);
			}
		}

		/**
		 * Immediately add a preload link to the page without waiting for the load command.
		 * Does not trigger an execution of the scripts, so it it can be done without regarding the load order.
		 * @param {string} path URL of resource to load
		 * @param {string} type Type of resource: https://developer.mozilla.org/en-US/docs/Web/HTML/Preloading_content
		 */
		preload(path:string, type:string) {
			let script:HTMLElement = document.createElement("link");
			script.setAttribute("rel", "preload");
			script.setAttribute("as", type);
			script.setAttribute("href", path);
			document.getElementsByTagName("head")[0].appendChild(script);
		}

		/**
		 * @param callback After getting this callback the caller can be assured
		 * that the contents of the loaded files are available to be executed.
		 * @param errorCallback Optional callback in case of errors. No need for
		 * this past development.
		 */
		public load(callback:any, errorCallback:any = undefined):void {
			this._callback = callback;
			this._errorCallback = errorCallback;
			this.loadNext();
		}

		private loadNext():void {
			// Avoid halting execution if am empty load queue is supplied
			if (this._loadCount >= this._sources.length) {
				if (this._callback) {
					this._callback();
				}
				return;
			}

			let script:HTMLElement;
			script = this._sources[this._loadCount];
			// console.log("ScriptLoader.loadNext: ", script.outerHTML);
			document.getElementsByTagName("head")[0].appendChild(script);
		}

		private onLoad(e:Event):void {
			this._loadCount++;
			// let VAR_HACK:string = "src";
			// let source:any = e.currentTarget.hasOwnProperty(VAR_HACK) ? e.currentTarget[VAR_HACK] : e.currentTarget;
			// console.log("ScriptLoader.onLoad:", source);
			this.loadNext();
		}

		private onError(e:ErrorEvent):void {
			this._loadCount++;
			console.error("ScriptLoader.onError:", e);
			if (this._errorCallback) {
				this._errorCallback(e);
			}
			this.loadNext();
		}
	}
}